import React from "react";
import Select, { type MultiValue, type Props as ReactSelectProps, type StylesConfig } from "react-select";

import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import cx from "classnames";
import { noop, throttle } from "lodash";
import useResizeObserver from "use-resize-observer";

import { nGetText } from "~/lib/utils";

import {
	defaultGetOptionValue,
	IndicatorsContainer,
	Input,
	type MultiSelectorOption,
	Option,
	Placeholder,
	reactSelectStylesRoot,
	sortSelectedToTop,
	ValueContainer,
} from "./MultiSelectorUtils";

import styles from "./MultiSelector.module.css";

declare module "csstype" {
	interface Properties {
		"--selector-width"?: string;
	}
}

interface MultiSelectorProps<T>
	extends Omit<
		ReactSelectProps<T, true>,
		| "menuPlacement"
		| "menuPortalTarget"
		| "isMulti"
		| "menuIsOpen"
		| "backspaceRemovesValue"
		| "controlShouldRenderValue"
		| "hideSelectedOptions"
		| "isClearable"
		| "tabSelectsValue"
		| "styles"
	> {
	/** If true, selected items will be sorted to the top of the flyout list */
	sortSelected?: boolean;
	/** Use smaller "condensed" style */
	condensed?: boolean;
	width?: string;
	/** What direction the flyout menu should open
	 * @see {@link https://www.radix-ui.com/primitives/docs/components/popover#content | Radix UI docs}
	 */
	menuSide?: Popover.PopoverContentProps["side"];
	/** Which side of the flyout menu should be aligned with the trigger
	 * @see {@link https://www.radix-ui.com/primitives/docs/components/popover#content | Radix UI docs}
	 */
	menuAlign?: Popover.PopoverContentProps["align"];
	/** Disable this selector
	 * @privateRemarks This is re-declared here so it shows up in Storybook
	 */
	isDisabled?: boolean;
	/** Max width of the menu flyout as a CSS value */
	menuMaxWidth?: string;
	/** Whether the placeholder should hide when the selector has values */
	buttonShouldRenderSelectionCount?: boolean;
	/** Component to use as the trigger button instead of the default styled button with the dropdown icon */
	CustomTriggerButton?: React.ForwardRefExoticComponent<
		React.ButtonHTMLAttributes<HTMLButtonElement> & React.RefAttributes<HTMLButtonElement>
	>;
	/** Fixed width for the content flyout */
	contentFixedWidth?: string;
	/** Whether the flyout menu is open */
	isOpen?: boolean;
}

export default function MultiSelector<T extends MultiSelectorOption>({
	sortSelected,
	components: customComponents,
	condensed,
	placeholder,
	value,
	width,
	getOptionValue = defaultGetOptionValue,
	options,
	className,
	menuSide,
	menuAlign,
	menuMaxWidth,
	buttonShouldRenderSelectionCount = true,
	CustomTriggerButton,
	contentFixedWidth,
	isOpen: controlledIsOpen,
	...rest
}: MultiSelectorProps<T>) {
	const { isDisabled } = rest;

	let reactSelectStyles = reactSelectStylesRoot as unknown as StylesConfig<T, true>;

	const [isOpen, setIsOpen] = React.useState(false);
	const [contentWidth, setContentWidth] = React.useState("auto");

	const values: MultiValue<T> = React.useMemo(() => (value ? ("length" in value ? value : [value]) : []), [value]);

	const toggleOpen = React.useCallback(() => setIsOpen(isOpen => !isOpen), []);

	const sortedOptions = React.useMemo(() => {
		if (!isOpen) return options;
		return sortSelectedToTop(options ?? [], values, getOptionValue, sortSelected ?? false);
	}, [isOpen, options, getOptionValue]); // eslint-disable-line react-hooks/exhaustive-deps -- specifically don't want this changing when values changes

	const buttonRef = React.useRef<HTMLButtonElement>(null);
	useResizeObserver<HTMLButtonElement>({
		ref: buttonRef,
		onResize: React.useMemo(
			() =>
				throttle(({ width }) => {
					if (!width || condensed) return;
					setContentWidth(`${width}px`);
				}, 200),
			[condensed],
		),
	});

	const toggleArrowKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLButtonElement>) => {
			if (event.key === "ArrowDown") toggleOpen();
			if (event.key === "Enter") event.preventDefault();
		},
		[toggleOpen],
	);

	const toggleEscape = React.useCallback(() => {
		setIsOpen(false);
		buttonRef.current?.focus();
	}, [buttonRef]);

	React.useEffect(() => {
		if (controlledIsOpen !== undefined) {
			setIsOpen(controlledIsOpen);
		}
	}, [controlledIsOpen]);

	return (
		<Popover.Root open={isOpen} onOpenChange={isDisabled ? noop : toggleOpen}>
			<Popover.Trigger asChild>
				{CustomTriggerButton ? (
					<CustomTriggerButton ref={buttonRef} onKeyDown={toggleArrowKeyDown} disabled={isDisabled} />
				) : (
					<button
						className={cx(className, condensed ? styles.targetCondensed : styles.targetOutline, {
							[condensed ? styles.targetCondensedActive : styles.targetOutlineActive]: isOpen && !isDisabled,
							[condensed ? styles.targetCondensedDisabled : styles.targetOutlineDisabled]: isDisabled,
						})}
						onKeyDown={toggleArrowKeyDown}
						style={{ "--selector-width": width ?? (condensed ? "auto" : "100%") }}
						ref={buttonRef}
					>
						{values.length === 0 || !buttonShouldRenderSelectionCount ? (
							<div className={styles.targetPlaceholder}>{placeholder}</div>
						) : (
							<div className={styles.targetValues}>
								{condensed ? (
									`${values.length} ${nGetText(values.length, "item", "items")} selected`
								) : (
									<div className={styles.targetItem}>
										<div className={styles.targetText}>
											{values.length} {nGetText(values.length, "selection", "selections")}
										</div>
									</div>
								)}
							</div>
						)}
						<div className={styles.targetDropdownIconContainer}>
							<FontAwesomeIcon icon={faCaretDown} className={styles.targetDropdownIcon} />
						</div>
					</button>
				)}
			</Popover.Trigger>
			<Popover.Content
				sideOffset={2}
				style={{ width: contentFixedWidth ?? contentWidth, maxWidth: menuMaxWidth }}
				onEscapeKeyDown={toggleEscape}
				className={styles.content}
				side={menuSide}
				align={menuAlign}
				data-ignore-focus-lock
			>
				<Select<T, true>
					{...rest}
					options={sortedOptions}
					getOptionValue={getOptionValue}
					value={value}
					isMulti
					menuIsOpen
					backspaceRemovesValue={false}
					controlShouldRenderValue={false}
					hideSelectedOptions={false}
					isClearable={false}
					tabSelectsValue={false}
					components={{ IndicatorsContainer, Input, Option, Placeholder, ValueContainer, ...customComponents }}
					styles={reactSelectStyles}
				/>
			</Popover.Content>
		</Popover.Root>
	);
}

export { components } from "react-select";
export type { MenuProps, MultiValue, ActionMeta } from "react-select";
export type { MultiSelectorOption };
